import type { PayloadAction } from '@reduxjs/toolkit';
import { extInfo } from 'lib/extInfo';
import { logger } from 'lib/logger';
import { IS_ONPREMISE } from 'reactApp/appHelpers/configHelpers';
import { CAMERA_UPLOADS_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import {
    type GalleryAnchorRequest,
    type GalleryListRequest,
    requestAnchorsList,
    requestGalleryList,
} from 'reactApp/modules/gallery/gallery.helpers';
import {
    changeGalleryFolder,
    galleryAnchorsLoadSuccess,
    galleryAnchorsRequest,
    galleryLoadAnchorListRequest,
    galleryLoadAnchorPaginatedListSuccess,
    galleryLoadCategoryRequest,
    galleryLoadMoreRequest,
    galleryLoadMoreSuccess,
    galleryLoadSuccess,
    galleryViewerLoadMoreRequest,
} from 'reactApp/modules/gallery/gallery.module';
import { routeChangeStart } from 'reactApp/modules/router/router.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import type { GetStateResponse } from 'reactApp/modules/router/router.types';
import { ESortOder } from 'reactApp/modules/sort/sort.types';
import { getLoadMoreLimit } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import {
    getCurrentGalleryCategory,
    getGalleryAnchorById,
    getGalleryAnchors,
    getGalleryCategory,
    getGalleryCursorByCategory,
    getGalleryFolder,
    getGalleryItemToAnchorId,
    getIsFastscrollEnabled,
    isGalleryCategoryLoaded,
} from './gallery.selectors';
import {
    type ChangeGalleryFolderAction,
    type GalleryAnchor,
    type GalleryAnchorsLoadRequestAction,
    type GalleryCategory,
    type GalleryLoadCategoryRequestAction,
    GalleryCategoryType,
} from './gallery.types';

const LOAD_LIMIT = getLoadMoreLimit(EStorageType.gallery);

// Порог для просмотрщика: сколько загруженных файлов должно оставаться в направлении листания
const VIEWER_LOAD_MORE_IF_LESS_THAN = 10;

function* fetchGalleryList({
    category = null,
    folder,
    storage,
    mtime_from,
    mtime_to,
    order,
    cursor,
    limit = LOAD_LIMIT,
}: {
    category: GalleryCategoryType | null;
    folder?: string;
    storage?: EStorageType;
    mtime_from?: number;
    mtime_to?: number;
    order?: ESortOder;
    limit?: number;
    cursor?: string;
}) {
    try {
        const isBizUser = yield select(UserSelectors.isBizUser);
        const eitherBizOrOnPremiseUser = isBizUser || IS_ONPREMISE;

        if (!category) {
            category = yield select(getGalleryCategory);
        }

        /**
         * storage перелающийся извне - вынужденная мера, так как при переходе из альбомов
         * в галерею galleryLoadRequest пушится раньше, чем обрабатывается установка getCurrentStorage
         * и в итоге внутри галереи мы начинали грузить вместо экстеншенов галереи экстеншены альбома
         * что было не неверно.
         * Экшн galleryLoadRequest диспатчится только из новой галереи, поэтом мы можем четко сказать
         * что здесь сторадж будет задан значение EStorageType.gallery
         */
        const currentStorage = storage || (yield select(getCurrentStorage));

        const categoryCursor = yield select(getGalleryCursorByCategory, category as GalleryCategoryType);
        const extensions =
            currentStorage === EStorageType.albums
                ? extInfo.getExtsForAlbums()?.join(',')
                : currentStorage === EStorageType.vigbo
                ? extInfo.getExtsForVigboAlbums()?.join(',')
                : extInfo.getExtsForGallery(eitherBizOrOnPremiseUser && category)?.join(',');

        const isFastScrollEnabled = yield select(getIsFastscrollEnabled);

        const data: GalleryListRequest = {
            extensions,
            cursor: cursor || categoryCursor,
            limit,
            mtime_from: mtime_from ? mtime_from / 1000 : undefined,
            mtime_to: mtime_to ? mtime_to / 1000 : undefined,
            order,
            ignore_mounts: isFastScrollEnabled,
        };

        if (!eitherBizOrOnPremiseUser) {
            if (category === GalleryCategoryType.mobile) {
                data.include = [CAMERA_UPLOADS_FOLDER_ID];
            } else if (category === GalleryCategoryType.desktop) {
                data.exclude = [CAMERA_UPLOADS_FOLDER_ID];
            }
        }

        if (folder) {
            data.include = [folder];
        }

        return yield call(requestGalleryList, data);
    } catch (error) {
        logger.error(error);
    }
}

function* fetchGalleryAnchors({ category }: { category: GalleryCategoryType | null }) {
    try {
        const isBizUser = yield select(UserSelectors.isBizUser);
        const eitherBizOrOnPremiseUser = isBizUser || IS_ONPREMISE;

        if (!category) {
            category = yield select(getGalleryCategory);
        }

        const extensions = extInfo.getExtsForGallery(eitherBizOrOnPremiseUser && category)?.join(',');

        const data: GalleryAnchorRequest = {
            extensions,
            // eslint-disable-next-line new-cap
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        if (category === GalleryCategoryType.mobile) {
            data.include = [CAMERA_UPLOADS_FOLDER_ID];
        } else if (category === GalleryCategoryType.desktop) {
            data.exclude = [CAMERA_UPLOADS_FOLDER_ID];
        }

        return yield call(requestAnchorsList, data);
    } catch (error) {
        logger.error(error);
    }
}

function* fetchGallery({
    category = GalleryCategoryType.all,
    folder,
    mtime_from,
    mtime_to,
}: {
    category: GalleryCategoryType;
    folder?: string;
    mtime_from?: number;
    mtime_to?: number;
}) {
    const isFastScrollEnabled = yield select(getIsFastscrollEnabled);

    if (isFastScrollEnabled) {
        yield fetchGalleryAnchors({ category });
    }

    return yield fetchGalleryList({ category, mtime_from, mtime_to, folder });
}

function* loadGalleryOnStart(action: PayloadAction<GetStateResponse>) {
    const storage = action.payload.storage;

    const isLoaded = yield select(isGalleryCategoryLoaded, GalleryCategoryType.all);

    if (storage !== EStorageType.gallery || (storage === EStorageType.gallery && isLoaded)) {
        return;
    }

    const data = yield fetchGalleryList({ category: GalleryCategoryType.all });
    yield put(galleryLoadSuccess({ category: GalleryCategoryType.all, data }));
}

function* loadAnchorList(action: ReturnType<typeof galleryLoadAnchorListRequest>) {
    const { anchorId, direction } = action.payload;

    const anchor: GalleryAnchor | undefined = yield select(getGalleryAnchorById, anchorId);
    if (!anchor) {
        return;
    }

    const category: GalleryCategory = yield select(getCurrentGalleryCategory);

    const order = direction === 'up' ? ESortOder.asc : ESortOder.desc;
    const cursor = order === ESortOder.asc ? anchor.cursorAsc : anchor.cursorDesc;

    const { galleryListV4 } = yield fetchGalleryList({
        category: category.id,
        order,
        mtime_from: anchor.periodStart,
        mtime_to: anchor.periodEnd,
        cursor: cursor || undefined,
        limit: LOAD_LIMIT / 2,
    });

    yield put(
        galleryLoadAnchorPaginatedListSuccess({
            anchorId,
            cursorOrder: order,
            data: galleryListV4,
        })
    );
}

function* loadMoreGallery() {
    const folder = yield select(getGalleryFolder);
    const data = yield fetchGalleryList({ category: null, folder });
    yield put(galleryLoadMoreSuccess({ data, folder, isExactFolder: Boolean(folder) }));
}

/**
 * Направление, в котором у якоря ещё есть незагруженные файлы.
 * Пустой курсор неоднозначен («ещё не грузили в этом порядке» либо «исчерпано»),
 * поэтому направление считается живым, если в нём ещё ничего не загружали.
 */
const getAnchorLoadDirection = (anchor: GalleryAnchor, direction: 'up' | 'down', remainingByDirection: number): 'up' | 'down' | null => {
    if (anchor.isLoaded || anchor.isLoading) {
        return null;
    }

    const canDown = (Boolean(anchor.cursorDesc) || anchor.topIds.length === 0) && remainingByDirection > 0;
    const canUp = (Boolean(anchor.cursorAsc) || anchor.bottomIds.length === 0) && remainingByDirection > 0;

    if (direction === 'down') {
        return canDown ? 'down' : null;
    }

    return canUp ? 'up' : null;
};

/**
 * Сколько загруженных файлов осталось в направлении листания внутри якоря
 * (порядок отображения: topIds, затем bottomIds в обратном порядке)
 */
const getRemainingInAnchor = (anchor: GalleryAnchor, itemId: string, direction: 'up' | 'down'): number => {
    const orderedIds = [...anchor.topIds, ...[...anchor.bottomIds].reverse()];
    const position = orderedIds.indexOf(itemId);

    if (position === -1) {
        return 0;
    }

    return direction === 'down' ? orderedIds.length - 1 - position : position;
};

/**
 * Дозагрузка для просмотрщика в фастскролл-галерее: если в направлении листания
 * осталось мало загруженных файлов — докручиваем курсор якоря текущего файла,
 * а когда он исчерпан — грузим ближайший соседний якорь.
 */
function* viewerLoadMore(action: ReturnType<typeof galleryViewerLoadMoreRequest>) {
    const { itemId, direction } = action.payload;

    const isFastScrollEnabled = yield select(getIsFastscrollEnabled);
    if (!isFastScrollEnabled) {
        return;
    }

    const category: GalleryCategory | undefined = yield select(getCurrentGalleryCategory);
    const anchors: GalleryAnchor[] = yield select(getGalleryAnchors);
    if (!category?.anchors?.length) {
        return;
    }

    const itemToAnchorId: Record<string, string | undefined> = yield select(getGalleryItemToAnchorId);
    const anchorId = itemToAnchorId[itemId];

    const anchorIdx = anchorId ? category.anchors.indexOf(anchorId) : -1;
    if (!anchorId || anchorIdx === -1) {
        return;
    }

    const anchor = anchors[anchorId];
    const remaining = getRemainingInAnchor(anchor, itemId, direction);

    if (remaining >= VIEWER_LOAD_MORE_IF_LESS_THAN) {
        return;
    }

    const loadDirection = getAnchorLoadDirection(anchor, direction, remaining);
    if (loadDirection) {
        yield put(galleryLoadAnchorListRequest({ anchorId, direction: loadDirection }));
        return;
    }

    // Текущий якорь исчерпан или уже грузится — пробуем ближайший соседний якорь (anchors[0] — самый новый период)
    const step = direction === 'down' ? 1 : -1;
    const neighbor = anchors[category.anchors[anchorIdx + step]];
    if (neighbor && !neighbor.isLoaded && !neighbor.isLoading) {
        yield put(galleryLoadAnchorListRequest({ anchorId: neighbor.id, direction }));
    }
}

function* loadAnchors(action: PayloadAction<GalleryAnchorsLoadRequestAction>) {
    const { category } = action.payload;
    const { anchors } = yield fetchGalleryAnchors({ category });
    yield put(galleryAnchorsLoadSuccess({ anchors }));
}

function* loadGalleryCategory(action: PayloadAction<GalleryLoadCategoryRequestAction>) {
    const { category } = action.payload;
    const data = yield fetchGallery({ category });
    yield put(galleryLoadSuccess({ category, data }));
}

function* loadGalleryFolder(action: PayloadAction<ChangeGalleryFolderAction>) {
    const { folder, category = GalleryCategoryType.all } = action.payload;
    const data = yield fetchGalleryList({ category, folder, cursor: '' });
    yield put(galleryLoadSuccess({ category, data, folder, isExactFolder: true }));
}

export function* watchGallery() {
    yield takeEvery(routeChangeStart.toString(), loadGalleryOnStart);

    yield takeLatest(galleryLoadMoreRequest.toString(), loadMoreGallery);
    yield takeEvery(galleryAnchorsRequest.toString(), loadAnchors);

    yield takeEvery(galleryLoadAnchorListRequest.toString(), loadAnchorList);
    yield takeEvery(galleryViewerLoadMoreRequest.toString(), viewerLoadMore);

    yield takeEvery(galleryLoadCategoryRequest.toString(), loadGalleryCategory);
    yield takeEvery(changeGalleryFolder.toString(), loadGalleryFolder);
}
